import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Award, Users, Target, Zap, Code, Brain, Wrench, User, ArrowRight } from 'lucide-react';
import { useEventTracking } from '../hooks/useEventTracking';
import { Card } from './ui/Button';
import { CONTACT_DETAILS } from '../config/contactDetails';
import { Helmet } from "react-helmet-async";

export const AboutPage: React.FC = () => {
    const { trackPageView, trackClick, isSessionReady } = useEventTracking();

    useEffect(() => {
        if (isSessionReady) {
            trackPageView('/about');
        }
    }, [isSessionReady]);

    const handleCTAClick = (ctaType: string) => {
        trackClick(`about-cta-${ctaType}`, `About ${ctaType} CTA`, 'button');
    };

    const values = [
        {
            icon: <Target className="h-8 w-8 text-primary-600" />,
            title: "Prevention First",
            description: `Focus on preventing problems rather than reacting to them. ${CONTACT_DETAILS.company.name} solutions emphasize early detection and proactive optimization across all industries.`
        },
        {
            icon: <Users className="h-8 w-8 text-primary-600" />,
            title: "Expert Partners",
            description: `${CONTACT_DETAILS.company.name} collaborates with machine learning specialists, industry advisors, and domain experts to deliver comprehensive solutions tailored for each sector.`
        },
        {
            icon: <Award className="h-8 w-8 text-primary-600" />,
            title: "Proven Technology",
            description: "Built on established predictive analytics principles and modern software architecture for reliable, scalable solutions that work across diverse industries."
        },
        {
            icon: <Zap className="h-8 w-8 text-primary-600" />,
            title: "Practical Innovation",
            description: "Using cutting-edge technology, but only when it solves real problems for real organizations. Innovation with purpose, not complexity for its own sake."
        }
    ];

    return (
        <>
            <Helmet>
                <title>About {CONTACT_DETAILS.company.name} | Cross-Industry Predictive Analytics | 25+ Years Experience</title>
                <meta name="description" content={`Learn about ${CONTACT_DETAILS.company.name}'s mission to transform data into actionable intelligence across industries. Expert-led predictive analytics with 25+ years software development experience.`} />
                <meta name="keywords" content="predictive analytics company, cross-industry AI, data intelligence solutions, machine learning consulting, British Columbia tech" />
                <link rel="canonical" href="https://4castlabs.com/about" />
                <meta property="og:title" content={`About ${CONTACT_DETAILS.company.name} | Cross-Industry Predictive Analytics`} />
                <meta property="og:description" content={`Learn about ${CONTACT_DETAILS.company.name}'s mission to transform data into actionable intelligence across industries with 25+ years software development experience.`} />
                <meta property="og:url" content="https://4castlabs.com/about" />
                <meta property="og:type" content="website" />
                <meta property="og:image" content="https://4castlabs.com/images/logo.png" />
            </Helmet>

            <div className="min-h-screen bg-gray-50 py-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Hero Section */}
                    <div className="text-center mb-16">
                        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                            About {CONTACT_DETAILS.company.name}
                        </h1>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            {CONTACT_DETAILS.company.name} transforms raw data into actionable intelligence across
                            multiple industries
                            using AI-powered predictive analytics and proven software development expertise.
                        </p>
                    </div>

                    {/* Mission Section with Founder's Note */}
                    <section className="mb-16">
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                            {/* Main Mission Content */}
                            <div className="lg:col-span-2">
                                <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Mission</h2>
                                <p className="text-lg text-gray-700 mb-4">
                                    Organizations across industries struggle with the same fundamental challenge: making critical
                                    decisions based on incomplete information or gut instinct instead of predictive intelligence.
                                    Whether it's healthcare resource allocation, manufacturing quality control, or agricultural
                                    yield optimization, the underlying problem is the same.
                                </p>
                                <p className="text-lg text-gray-700 mb-4">
                                    There's a better way. By combining 25+ years of software development experience with
                                    machine learning partnerships and industry advisors, {CONTACT_DETAILS.company.name} is working to make
                                    predictive analytics accessible across diverse sectors that want to move beyond reactive
                                    decision-making.
                                </p>
                                <p className="text-lg text-gray-700">
                                    The goal: help organizations identify patterns and predict outcomes early, so they can
                                    optimize resources strategically, not reactively.
                                </p>

                                <div className="bg-gradient-to-br from-primary-100 to-primary-200 rounded-2xl p-8 mt-8">
                                    <div className="text-center">
                                        <div className="text-4xl font-bold text-primary-800 mb-2">Cross-Industry</div>
                                        <div className="text-primary-700 mb-4">Methodology</div>
                                        <div className="text-4xl font-bold text-primary-800 mb-2">25+ Years</div>
                                        <div className="text-primary-700 mb-4">Software Experience</div>
                                        <div className="text-4xl font-bold text-primary-800 mb-2">AI-Powered</div>
                                        <div className="text-primary-700">Predictive Analytics</div>
                                    </div>
                                </div>
                            </div>

                            {/* Founder's Note Sidebar */}
                            <div className="lg:col-span-1">
                                <Card
                                    className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200 sticky top-8 cursor-pointer hover:shadow-lg transition-shadow"
                                    onClick={() => trackClick('founders-note', 'Founders Note', 'section')}
                                >
                                    <div className="flex items-center mb-4">
                                        <div className="bg-blue-100 p-2 rounded-full mr-3">
                                            <User className="h-5 w-5 text-blue-600"/>
                                        </div>
                                        <h3 className="text-lg font-semibold text-gray-900">Founder's Note</h3>
                                    </div>

                                    <div className="space-y-4 text-sm text-gray-700 leading-relaxed">
                                        <p>
                                            <strong>Why I started {CONTACT_DETAILS.company.name}:</strong> After 25+ years building mission-critical
                                            software systems - including medical AI for risk prediction and enterprise applications
                                            serving millions of users - I saw how predictive analytics principles could transform
                                            decision-making across multiple industries, not just healthcare.
                                        </p>

                                        <p>
                                            Working in healthcare taught me that early detection and pattern recognition save
                                            lives <em>and</em> money. The same principles apply across sectors: predict problems
                                            before they become crises, optimize resources before waste occurs, and preserve
                                            critical knowledge before expertise walks out the door.
                                        </p>

                                        <p>
                                            My background is specifically in building <strong>reliable systems that can't fail</strong>
                                            {' '}- exactly what organizations need when making critical decisions.
                                        </p>

                                        <p>
                                            Current focus: I'm seeking pilot partners across different industries to validate
                                            this cross-sector approach with real-world implementations. My network includes
                                            AMII machine learning specialists, engineering advisors, and industry partners who
                                            provide domain expertise to complement my software development skills.
                                        </p>
                                    </div>
                                </Card>
                            </div>
                        </div>
                    </section>

                    {/* Values Section */}
                    <section className="mb-16">
                        <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">{CONTACT_DETAILS.company.name} Approach</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                            {values.map((value, index) => (
                                <Card key={index} className="p-6 text-center hover:shadow-lg transition-shadow">
                                    <div className="flex justify-center mb-4">
                                        {value.icon}
                                    </div>
                                    <h3 className="text-xl font-semibold text-gray-900 mb-3">
                                        {value.title}
                                    </h3>
                                    <p className="text-gray-600">
                                        {value.description}
                                    </p>
                                </Card>
                            ))}
                        </div>
                    </section>

                    {/* Technology Section */}
                    <section className="mb-16 bg-white rounded-2xl p-8 shadow-lg">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                            <div>
                                <h2 className="text-3xl font-bold text-gray-900 mb-6">Technology & Development Approach</h2>
                                <div className="mb-6">
                                    The software development expertise at {CONTACT_DETAILS.company.name} enables building reliable, scalable
                                    systems using modern web technologies. Working with specialized partners, complete
                                    predictive analytics solutions can be delivered across any industry.
                                </div>
                                <div className="space-y-4">
                                    <div className="flex items-start">
                                        <div className="bg-primary-100 p-2 rounded-lg mr-4 flex-shrink-0">
                                            <Code className="h-5 w-5 text-primary-600"/>
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-gray-900 mb-1">Modern Web Architecture</h3>
                                            <p className="text-gray-600">Rust-based backends with React frontends -
                                                core expertise for building reliable, fast applications</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start">
                                        <div className="bg-primary-100 p-2 rounded-lg mr-4 flex-shrink-0">
                                            <Brain className="h-5 w-5 text-primary-600"/>
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-gray-900 mb-1">ML Integration</h3>
                                            <p className="text-gray-600">Partnering with AMII specialists for advanced
                                                analytics and model development across industries</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start">
                                        <div className="bg-primary-100 p-2 rounded-lg mr-4 flex-shrink-0">
                                            <Wrench className="h-5 w-5 text-primary-600"/>
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-gray-900 mb-1">Industry Expertise</h3>
                                            <p className="text-gray-600">Collaborating with domain experts and industry
                                                leaders for practical, sector-specific solutions</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-gray-50 rounded-xl p-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Compatible Systems</h3>
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div className="bg-white p-3 rounded-lg">
                                        <div className="font-medium text-gray-900">Healthcare</div>
                                        <div className="text-gray-600">EHR, PACS, Laboratory</div>
                                    </div>
                                    <div className="bg-white p-3 rounded-lg">
                                        <div className="font-medium text-gray-900">Manufacturing</div>
                                        <div className="text-gray-600">PLC, SCADA, ERP</div>
                                    </div>
                                    <div className="bg-white p-3 rounded-lg">
                                        <div className="font-medium text-gray-900">Agriculture</div>
                                        <div className="text-gray-600">IoT Sensors, Weather</div>
                                    </div>
                                    <div className="bg-white p-3 rounded-lg">
                                        <div className="font-medium text-gray-900">Energy</div>
                                        <div className="text-gray-600">SCADA, Grid Systems</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Visual Section */}
                    <section className="mb-16">
                        <div className="bg-white rounded-2xl p-8 shadow-lg">
                            <h2 className="text-2xl font-bold text-center text-gray-900 mb-6">
                                Turning Data Into Actionable Insights Across Industries
                            </h2>
                            <div className="flex flex-col md:flex-row gap-6 mb-6">
                                <div className="flex-1">
                                    <div className="h-80 overflow-hidden rounded-xl shadow-lg mb-4">
                                        <img
                                            src='/images/dashboard.webp'
                                            alt="Predictive Analytics Dashboard"
                                            className="w-full h-full object-cover"
                                            loading="lazy"
                                        />
                                    </div>
                                    <div className="text-center">
                                        <div className="text-lg font-semibold text-gray-900">Predictive Analytics
                                            Dashboard
                                        </div>
                                        <div className="text-sm text-gray-600">Real-time insights across sectors</div>
                                    </div>
                                </div>
                                <div className="flex-1">
                                    <div className="h-80 overflow-hidden rounded-xl shadow-lg mb-4">
                                        <img
                                            src='/images/expert.webp'
                                            alt="Expert Implementation"
                                            className="w-full h-full object-cover"
                                            loading="lazy"
                                        />
                                    </div>
                                    <div className="text-center">
                                        <div className="text-lg font-semibold text-gray-900">Expert Implementation</div>
                                        <div className="text-sm text-gray-600">Industry-specific deployment</div>
                                    </div>
                                </div>
                            </div>
                            <p className="text-center text-gray-600 mt-4">
                                Our platform transforms complex data from any industry into clear, actionable
                                recommendations for your team.
                            </p>
                        </div>
                    </section>

                    {/* Expertise & Partners Section */}
                    <section className="mb-16">
                        <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Background & Collaborative Network</h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            <Card className="p-6 text-center">
                                <div className="w-16 h-16 bg-primary-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                                    <Code className="h-8 w-8 text-primary-600"/>
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-3">Core Expertise</h3>
                                <p className="text-gray-600 text-sm">
                                    25+ years building scalable web applications, data systems, and modern cloud
                                    infrastructure. Expertise in Rust, React, and enterprise-grade solutions for
                                    mission-critical applications across industries.
                                </p>
                            </Card>

                            <Card className="p-6 text-center">
                                <div className="w-16 h-16 bg-primary-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                                    <Brain className="h-8 w-8 text-primary-600"/>
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-3">Machine Learning Partners</h3>
                                <p className="text-gray-600 text-sm">
                                    Collaborating with AMII specialists and data scientists to develop and integrate
                                    predictive models tailored to diverse industry applications and requirements.
                                </p>
                            </Card>

                            <Card className="p-6 text-center">
                                <div className="w-16 h-16 bg-primary-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                                    <Wrench className="h-8 w-8 text-primary-600"/>
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-3">Industry Advisors</h3>
                                <p className="text-gray-600 text-sm">
                                    Working with executives and domain experts across healthcare, manufacturing,
                                    agriculture, energy, and other sectors to ensure solutions meet real-world requirements.
                                </p>
                            </Card>
                        </div>

                        <div className="bg-white rounded-2xl p-8 shadow-lg mt-8 max-w-3xl mx-auto">
                            <h3 className="text-xl font-semibold text-gray-900 mb-4 text-center">Based in British Columbia</h3>
                            <p className="text-gray-600">
                                <span className="block mb-4">Serving clients across Western Canada with remote capabilities and
                                on-site consultation when needed. The collaborative network spans software development,
                                machine learning research, and industry expertise across multiple sectors.</span>
                                <span className="block mb-4">We help organizations of all sizes prevent costly failures and
                                optimize resources through intelligent monitoring and predictive insights.</span>
                                <span className="block mb-4">
                                    <strong>Current focus:</strong> Seeking pilot partners across different industries to
                                    validate cross-sector methodology through smart monitoring and predictive analytics.
                                </span>
                                <span className="block">
                                    Right-sized solutions. Direct access to the development team. No vendor lock-in.
                                </span>
                            </p>
                        </div>
                    </section>

                    {/* Pilot Program Section */}
                    <section className="mb-16 bg-gradient-to-r from-green-600 to-green-800 text-white rounded-2xl p-10">
                        <div className="text-center mb-6">
                            <h2 className="text-3xl font-bold mb-4">Currently Seeking Pilot Partners Across Industries</h2>
                            <p className="text-xl text-green-100">
                                {CONTACT_DETAILS.company.name} is looking for 2-3 forward-thinking organizations across different sectors
                                to partner with for initial cross-industry implementations. These pilots will help prove
                                the concept while providing significant value to participating companies.
                            </p>
                        </div>

                        <div className="grid md:grid-cols-3 gap-6 mb-8">
                            <div className="text-center">
                                <div className="bg-green-500 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-3">
                                    <span className="text-white font-bold">1</span>
                                </div>
                                <h3 className="font-semibold mb-2">Choose One System</h3>
                                <p className="text-sm text-green-100">Pick your most critical process or decision point</p>
                            </div>
                            <div className="text-center">
                                <div className="bg-green-500 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-3">
                                    <span className="text-white font-bold">2</span>
                                </div>
                                <h3 className="font-semibold mb-2">3-6 Month Trial</h3>
                                <p className="text-sm text-green-100">Full setup, monitoring, and predictive insights</p>
                            </div>
                            <div className="text-center">
                                <div className="bg-green-500 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-3">
                                    <span className="text-white font-bold">3</span>
                                </div>
                                <h3 className="font-semibold mb-2">Proven ROI</h3>
                                <p className="text-sm text-green-100">Scale to more systems or walk away</p>
                            </div>
                        </div>

                        <div className="text-center">
                            <Link
                                to="/contact?source=about-pilot"
                                className="bg-white text-green-700 px-8 py-4 rounded-xl font-semibold text-lg hover:bg-gray-50 transition-colors inline-flex items-center"
                                onClick={() => handleCTAClick('pilot-program')}
                            >
                                Ask About Our Pilot Program
                                <ArrowRight className="ml-2 h-5 w-5" />
                            </Link>
                        </div>
                    </section>
                </div>
            </div>
        </>
    );
};